import { store } from '../store';
import { setCredentials, logout } from '../store/authSlice';
import { getEnv } from './env';

function getEffectiveApiBase(): string {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const queryApiBase = urlParams.get('apiBase');
    if (queryApiBase) {
      return queryApiBase;
    }
  }
  return getEnv('VITE_API_BASE', 'http://localhost:3000');
}

export const API_BASE = getEffectiveApiBase();

/**
 * Resolves a tile URL (which may be relative like `/api/tiles/...` or absolute like `https://api.trailrender.com/api/tiles/...`)
 * against the current `API_BASE` endpoint (e.g. `http://backend:3000` during Puppeteer rendering).
 */
export function resolveTileUrl(url: string): string {
  if (!url) return url;

  if (url.startsWith('/')) {
    if (!API_BASE || API_BASE === '/') return url;
    return `${API_BASE.replace(/\/$/, '')}${url}`;
  }

  if (API_BASE && (url.startsWith('http://') || url.startsWith('https://'))) {
    const match = url.match(/^(https?:\/\/[^/]+)(\/.*)$/);
    if (match) {
      const originalHost = match[1];
      const pathAndQuery = match[2];
      const apiOrigin = API_BASE.replace(/\/$/, '');

      if (originalHost !== apiOrigin) {
        return `${apiOrigin}${pathAndQuery}`;
      }
    }
  }

  return url;
}


let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

/**
 * Base fetch client that automatically manages Bearer tokens and handles
 * Token Rotation / Auto-refresh upon receiving 401 Unauthorized responses.
 */
export async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const state = store.getState();
  const token = state.auth.token;

  const headers = new Headers(options.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Ensure JSON content-type is set if sending a body and not already specified
  if (options.body && !headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const requestOptions: RequestInit = {
    credentials: 'include',
    ...options,
    headers,
  };

  const response = await fetch(url, requestOptions);

  if (
    response.status === 401 &&
    !url.includes('/api/auth/refresh') &&
    !url.includes('/api/auth/google') &&
    !url.includes('/api/auth/strava') &&
    !url.includes('/impersonate')
  ) {
    // Access token expired, attempt to refresh
    if (!isRefreshing) {
      isRefreshing = true;

      try {
        const refreshRes = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include', // Crucial to send HttpOnly cookies
        });

        if (refreshRes.ok) {
          const data = await refreshRes.json();
          const newAccessToken = data.accessToken;

          store.dispatch(setCredentials({ token: newAccessToken }));
          isRefreshing = false;
          onRefreshed(newAccessToken);
        } else {
          // Refresh failed (cookie expired/invalid), trigger logout
          isRefreshing = false;
          store.dispatch(logout());
          window.location.href = '/login';
          throw new Error('Session expired. Please log in again.');
        }
      } catch (refreshErr) {
        isRefreshing = false;
        store.dispatch(logout());
        window.location.href = '/login';
        throw refreshErr;
      }
    }

    // Queue current request to run after token refresh completes
    return new Promise<Response>((resolve) => {
      subscribeTokenRefresh((newToken) => {
        headers.set('Authorization', `Bearer ${newToken}`);
        resolve(fetch(url, requestOptions));
      });
    });
  }

  return response;
}

/**
 * Convenience method for GET requests, parses JSON response automatically.
 */
export async function apiGet<T = any>(path: string): Promise<T> {
  const response = await apiFetch(path, { method: 'GET' });
  if (!response.ok) {
    const errText = await response.text();
    let errJson;
    try {
      errJson = JSON.parse(errText);
    } catch {}
    throw new Error(errJson?.message || errJson?.error || `GET ${path} failed with status ${response.status}`);
  }
  return response.json();
}

/**
 * Convenience method for POST requests.
 */
export async function apiPost<T = any>(path: string, body?: any): Promise<T> {
  const response = await apiFetch(path, {
    method: 'POST',
    body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
  });
  if (!response.ok) {
    const errText = await response.text();
    let errJson;
    try {
      errJson = JSON.parse(errText);
    } catch {}
    throw new Error(errJson?.message || errJson?.error || `POST ${path} failed with status ${response.status}`);
  }
  return response.json();
}

/**
 * Convenience method for PATCH requests.
 */
export async function apiPatch<T = any>(path: string, body?: any): Promise<T> {
  const response = await apiFetch(path, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const errText = await response.text();
    let errJson;
    try {
      errJson = JSON.parse(errText);
    } catch {}
    throw new Error(errJson?.message || errJson?.error || `PATCH ${path} failed with status ${response.status}`);
  }
  return response.json();
}

/**
 * Convenience method for DELETE requests.
 */
export async function apiDelete<T = any>(path: string): Promise<T> {
  const response = await apiFetch(path, { method: 'DELETE' });
  if (!response.ok) {
    const errText = await response.text();
    let errJson;
    try {
      errJson = JSON.parse(errText);
    } catch {}
    throw new Error(errJson?.message || errJson?.error || `DELETE ${path} failed with status ${response.status}`);
  }
  if (response.status === 204) {
    return {} as any;
  }
  const text = await response.text();
  return text ? JSON.parse(text) : ({} as any);
}

export async function exitImpersonation(): Promise<{ accessToken: string }> {
  const res = await apiFetch('/api/auth/exit-impersonation', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to exit impersonation session');
  }
  return res.json();
}
